import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Badge, Button, PageHeader, Alert, Select, EmptyState } from '../../ui/components';
import { FileText, Edit, Send, Search, CheckCircle, Landmark, CircleCheck, PartyPopper, Plus, Check, Clock, Info } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { BORROWER_NAV } from '../../config/navigation';
import { supabase } from '../../lib/supabase';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { useEarlyAccess } from '../../components/common/EarlyAccessProvider';

const statusSteps = [
  { id: 'draft', label: 'Application Started', icon: FileText },
  { id: 'in_progress', label: 'In Progress', icon: Edit },
  { id: 'submitted_for_underwriting', label: 'Submitted for Review', icon: Send },
  { id: 'underwriting_in_progress', label: 'Under Review', icon: Search },
  { id: 'cleared_for_marketplace', label: 'Ready for Lenders', icon: CheckCircle },
  { id: 'in_marketplace', label: 'In Marketplace', icon: Landmark },
  { id: 'bid_accepted', label: 'Offer Accepted', icon: CircleCheck },
  { id: 'closed', label: 'Funded', icon: PartyPopper },
];

const BorrowerStatus = () => {
  const navigate = useNavigate();
  const { startApplication } = useEarlyAccess();
  const [borrower, setBorrower] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for demo session first
      const demoSession = localStorage.getItem('lk_demo_session');
      if (demoSession) {
        const demoUser = JSON.parse(demoSession);
        setBorrower({
          id: demoUser.id,
          email: demoUser.email,
          firstName: demoUser.user_metadata?.firstName || 'Demo',
          lastName: demoUser.user_metadata?.lastName || 'Borrower',
        });
        await fetchApplications(demoUser.id);
        return;
      }

      // Otherwise check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/borrower/login');
        return;
      }

      setBorrower({
        id: session.user.id,
        email: session.user.email,
        firstName: session.user.user_metadata?.firstName || session.user.email.split('@')[0],
        lastName: session.user.user_metadata?.lastName || '',
      });

      await fetchApplications(session.user.id);
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/borrower/login');
    }
  };

  const DEMO_APPLICATIONS = [
    {
      id: 'demo-app-001', user_id: 'demo-borrower-123', anonymous_id: 'LK-7842',
      status: 'in_progress', product_type: 'conventional', loan_amount: 385000,
      readiness_score: 62,
      property_info: { address: '742 Evergreen Terrace, Springfield' },
      created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
      updated_at: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: 'demo-app-002', user_id: 'demo-borrower-123', anonymous_id: 'LK-5219',
      status: 'draft', product_type: 'fha', loan_amount: 275000,
      readiness_score: 34,
      property_info: { address: '' },
      created_at: new Date(Date.now() - 10 * 86400000).toISOString(),
      updated_at: new Date(Date.now() - 8 * 86400000).toISOString(),
    },
  ];

  const fetchApplications = async (userId) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      let apps = data || [];

      if (apps.length === 0 && userId === 'demo-borrower-123') {
        apps = DEMO_APPLICATIONS;
      }

      setApplications(apps);
      if (apps.length > 0) {
        setSelectedApp(apps[0]);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      if (userId === 'demo-borrower-123') {
        setApplications(DEMO_APPLICATIONS);
        setSelectedApp(DEMO_APPLICATIONS[0]);
      } else {
        setApplications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('lk_demo_session');
    await supabase.auth.signOut();
    navigate('/');
  };

  const getCurrentStepIndex = (status) => {
    const index = statusSteps.findIndex(step => step.id === status);
    return index >= 0 ? index : 0;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!borrower) return null;

  const currentStepIndex = selectedApp ? getCurrentStepIndex(selectedApp.status) : 0;

  return (
    <AppLayout
      role="borrower"
      navItems={BORROWER_NAV}
      user={{
        name: `${borrower.firstName} ${borrower.lastName}`,
        email: borrower.email,
        role: 'borrower',
      }}
      onLogout={handleLogout}
    >
      <div>
        <PageHeader
          title="Application Status"
          description="Track your application progress"
        />

        {loading ? (
          <Surface style={{ textAlign: 'center', padding: 'var(--lk-space-10)' }}>
            <div className="lk-spinner lk-spinner-md" style={{ margin: '0 auto var(--lk-space-3)' }} />
            Loading status...
          </Surface>
        ) : applications.length > 0 ? (
          <>
            {/* Application Selector */}
            {applications.length > 1 && (
              <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
                <Select
                  label="Select Application"
                  value={selectedApp?.id || ''}
                  onChange={(e) => setSelectedApp(applications.find(a => a.id === e.target.value))}
                >
                  {applications.map(app => (
                    <option key={app.id} value={app.id}>
                      {app.property_info?.address || 'New Application'} - {formatDate(app.created_at)}
                    </option>
                  ))}
                </Select>
              </Surface>
            )}

            {selectedApp && (
              <>
                {/* Current Status Card */}
                <Surface padding="var(--lk-space-6)" style={{ marginBottom: 'var(--lk-space-4)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>Current Status</div>
                      <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                        <Badge variant="primary" style={{ fontSize: 'var(--lk-text-base)', padding: '6px 14px' }}>
                          {statusSteps[currentStepIndex]?.label}
                        </Badge>
                      </div>
                      <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)' }}>
                        Last updated: {formatDate(selectedApp.updated_at || selectedApp.created_at)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      {(() => { const StepIcon = statusSteps[currentStepIndex]?.icon; return StepIcon ? <StepIcon size={56} color="var(--lk-primary-500)" /> : null; })()}
                      <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-xs)', marginTop: 4 }}>Step {currentStepIndex + 1} of {statusSteps.length}</div>
                    </div>
                  </div>
                </Surface>

                {/* Progress Timeline */}
                <Surface padding="var(--lk-space-6)" style={{ marginBottom: 'var(--lk-space-4)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-5)' }}>Application Journey</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-4)' }}>
                    {statusSteps.map((step, index) => {
                      const StepIcon = step.icon;
                      const isActive = index <= currentStepIndex;
                      const isCurrent = index === currentStepIndex;
                      return (
                        <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-3)' }}>
                          <div style={{
                            width: 44, height: 44, borderRadius: 'var(--lk-radius-full)', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: isActive ? 'var(--lk-primary-500)' : 'var(--lk-gray-100)',
                            color: isActive ? 'white' : 'var(--lk-text-quaternary)',
                          }}>
                            <StepIcon size={22} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--lk-text-primary)' : 'var(--lk-text-quaternary)' }}>
                              {step.label}
                            </div>
                            {isCurrent && <Badge variant="success" style={{ marginTop: 4 }}>Current Step</Badge>}
                            {index < currentStepIndex && (
                              <div style={{ color: 'var(--lk-success-600)', fontSize: 'var(--lk-text-xs)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                                <Check size={12} /> Completed
                              </div>
                            )}
                            {index > currentStepIndex && (
                              <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-xs)', marginTop: 2 }}>Pending</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Surface>

                {/* Next Steps */}
                <Surface padding="var(--lk-space-6)">
                  <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>What's Next?</div>
                  {selectedApp.status === 'draft' || selectedApp.status === 'in_progress' ? (
                    <Alert variant="info" icon={Info}>
                      <strong>Complete your application</strong> — Finish filling out all required information and submit for underwriting review.
                      <div style={{ marginTop: 'var(--lk-space-2)' }}>
                        <Button variant="primary" size="sm" onClick={() => navigate(`/apply?resume=${selectedApp.id}`)}>
                          Continue Application
                        </Button>
                      </div>
                    </Alert>
                  ) : selectedApp.status === 'submitted_for_underwriting' || selectedApp.status === 'underwriting_in_progress' ? (
                    <Alert variant="warning" icon={Clock}>
                      <strong>Owen is Reviewing</strong> — Owen is running a pre-underwriting review on your file. You'll be notified the moment the verdict is ready.
                    </Alert>
                  ) : selectedApp.status === 'in_marketplace' ? (
                    <Alert variant="info" icon={Landmark}>
                      <strong>Shared with Lenders</strong> — Your file is live in the lender network. You'll receive notifications as lenders submit offers.
                    </Alert>
                  ) : selectedApp.status === 'bid_accepted' ? (
                    <Alert variant="success" icon={CircleCheck}>
                      <strong>Offer Accepted</strong> — Congratulations! You've accepted a lender's offer. Next steps will be sent to your email.
                    </Alert>
                  ) : selectedApp.status === 'closed' ? (
                    <Alert variant="success" icon={PartyPopper}>
                      <strong>Funded!</strong> — Congratulations! Your loan has been funded. Welcome to your new home!
                    </Alert>
                  ) : (
                    <Alert variant="info" icon={Info}>
                      Your application is progressing. We'll keep you updated on the next steps.
                    </Alert>
                  )}
                </Surface>
              </>
            )}
          </>
        ) : (
          <EmptyState
            icon={FileText}
            title="No Applications Yet"
            description="Start your mortgage journey by creating your first application."
            action={<Button variant="primary" icon={Plus} onClick={() => startApplication('borrower_status_empty')}>Start New Application</Button>}
          />
        )}
      </div>
      <FloatingOwenAssistant context="borrower-status" />
      <FloatingKnowledge context="general" />
    </AppLayout>
  );
};

export default BorrowerStatus;
